import { useEffect, useId, useMemo, useState } from "react";
import { INVITE_ROLES } from "./inviteConstants";

/** Select value when participant uses a title not in the workspace list. */
export const INVITE_ROLE_CUSTOM = "__invite_role_custom__";

function matchRole(value, roles) {
  const wanted = String(value || "").trim().toLowerCase();
  if (!wanted) return "";
  return roles.find((r) => r.toLowerCase() === wanted) || "";
}

/**
 * Workspace role picker: native dropdown + optional custom title field.
 */
export default function InviteRoleField({
  value,
  onChange,
  roleOptions,
  disabled = false,
  className = "",
  selectClassName = "admin-filter-select w-full border rounded-lg px-3 py-2 md:max-w-md",
  showHint = true,
  id: idProp,
}) {
  const autoId = useId();
  const selectId = idProp || `invite-role-${autoId}`;
  const customId = `${selectId}-custom`;

  const roles = useMemo(
    () => (roleOptions?.length ? roleOptions : INVITE_ROLES),
    [roleOptions]
  );

  const matched = useMemo(() => matchRole(value, roles), [value, roles]);
  const isCustomValue = Boolean(String(value || "").trim()) && !matched;

  const [customOpen, setCustomOpen] = useState(isCustomValue);

  useEffect(() => {
    if (isCustomValue) setCustomOpen(true);
  }, [isCustomValue]);

  const selectValue = matched || (customOpen || isCustomValue ? INVITE_ROLE_CUSTOM : "");

  const handleSelect = (e) => {
    const next = e.target.value;
    if (next === INVITE_ROLE_CUSTOM) {
      setCustomOpen(true);
      if (matched) onChange("");
      return;
    }
    setCustomOpen(false);
    onChange(next);
  };

  const showCustomInput = customOpen || isCustomValue;

  return (
    <div className={className}>
      <select
        id={selectId}
        className={selectClassName}
        value={selectValue}
        onChange={handleSelect}
        disabled={disabled}
        aria-describedby={showHint ? `${selectId}-hint` : undefined}
      >
        <option value="">Select role…</option>
        {roles.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
        <option value={INVITE_ROLE_CUSTOM}>Other (custom title)…</option>
      </select>
      {showCustomInput ? (
        <input
          id={customId}
          type="text"
          className={`${selectClassName} mt-2`}
          value={matched ? "" : value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type custom job title"
          disabled={disabled}
          autoComplete="off"
          aria-label="Custom job title"
        />
      ) : null}
      {showHint ? (
        <p id={`${selectId}-hint`} className="text-xs text-slate-400 mt-1">
          Choose a workspace title or pick Other to enter a custom role.
        </p>
      ) : null}
    </div>
  );
}
